import React from 'react';
import { ImageUpload } from './ui/image-upload';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Camera } from 'lucide-react';


interface AvatarUploadProps {
    currentAvatar?: string;
    onAvatarUpdated?: (newAvatarUrl: string) => void;
    size?: 'sm' | 'md' | 'lg';
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
    currentAvatar,
    onAvatarUpdated,
    size = 'md',
}) => {
    const { user } = useAuth();
    const [showUpload, setShowUpload] = React.useState(false);

    const sizeClasses = {
        sm: 'w-16 h-16',
        md: 'w-24 h-24',
        lg: 'w-32 h-32',
    };

    const handleAvatarUpload = async (file: File) => {
        const result = await apiService.uploadAvatar(file);
        return result;
    };

    const handleUploadSuccess = (result: any) => {
        if (result.success && result.image) {
            const newAvatarUrl = result.image.cdnUrl || result.image.url;

            // Update user context
            // updateUser({ avatar: newAvatarUrl });

            // Callback for parent component
            onAvatarUpdated?.(newAvatarUrl);

            setShowUpload(false);
        }
    };

    const handleUploadError = (error: string) => {
        console.error('Avatar upload failed:', error);
        // You might want to show a toast notification here
    };

    if (showUpload) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Upload New Avatar</h3>
                    <Button
                        variant="outline"
                        onClick={() => setShowUpload(false)}
                    >
                        Cancel
                    </Button>
                </div>
                <ImageUpload
                    onUpload={handleAvatarUpload}
                    onSuccess={handleUploadSuccess}
                    onError={handleUploadError}
                    accept="image/*"
                    maxSize={5}
                    preview={true}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center space-y-4">
            <div className="relative">
                <Avatar className={sizeClasses[size]}>
                    <AvatarImage
                        src={currentAvatar || user?.avatar}
                        alt={user?.name || 'User avatar'}
                    />
                    <AvatarFallback>
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                </Avatar>

                <Button
                    size="sm"
                    variant="secondary"
                    className="absolute -bottom-2 -right-2 rounded-full p-2"
                    onClick={() => setShowUpload(true)}
                >
                    <Camera className="w-4 h-4" />
                </Button>
            </div>

            <Button
                variant="outline"
                onClick={() => setShowUpload(true)}
            >
                Change Avatar
            </Button>
        </div>
    );
};